///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Principal } from '../../shared/auth/principal.service';
import { GtthNotificationsService } from '../../shared/notifications/notifications.service';
import { RunningDesignDTO, UserDTO } from '../../generated/shared-types.model';
import { UserAuthorities } from '../../services/user/user-authorities';

@Injectable({
    providedIn: 'root'
})
export class GtthRunningDesignInfoService {
    public static readonly ORDINARY_REFRESH_TIMEOUT = 60000;
    public static readonly FREQUENT_REFRESH_TIMEOUT = 5000;
    private readonly baseUrl: string = 'api/designs/current';
    private refreshNotificationsInterval$: BehaviorSubject<number>;

    constructor(
        private readonly http: HttpClient,
        private readonly principal: Principal,
        private readonly notificationsService: GtthNotificationsService
    ) {}

    setRefreshNotificationsInterval$(interval: number): void {
        this.refreshNotificationsInterval$?.next(interval);
    }

    getIsAnyDesignRunning(): Observable<boolean> {
        return this.getRunningDesign().pipe(
            map((runningDesign: RunningDesignDTO) => {
                this.notificationsService.clearNotifications();

                if (runningDesign?.projectId) {
                    this.createRunningDesignNotification(runningDesign);
                    return true;
                }
                return false;
            })
        );
    }

    private getRunningDesign(): Observable<RunningDesignDTO> {
        return this.principal.getAuthenticationState().pipe(
            switchMap((user: UserDTO) => {
                return user != null ? this.userIsAuthenticated() : this.userIsNotAuthenticated();
            })
        );
    }

    private createRunningDesignNotification(runningDesign: RunningDesignDTO): void {
        let notificationText = `Running design for Project '${runningDesign.projectName}'`;

        this.principal.hasAnyAuthority(UserAuthorities.ALL_SIKLU).then((isSikluUser: boolean) => {
            if (isSikluUser) {
                notificationText += `, Сompany '${runningDesign.customerName}'`;
            }
            this.notificationsService.addNotification({
                text: notificationText,
                progress: runningDesign.progress,
                projectId: runningDesign.projectId
            });
        });
    }

    private getRunningDesignInfo(): Observable<RunningDesignDTO> {
        return this.http.get<RunningDesignDTO>(this.baseUrl);
    }

    private userIsAuthenticated(): Observable<RunningDesignDTO> {
        this.refreshNotificationsInterval$ = new BehaviorSubject(GtthRunningDesignInfoService.ORDINARY_REFRESH_TIMEOUT);
        return this.refreshNotificationsInterval$.pipe(
            switchMap((time: number) => {
                return timer(0, time);
            }),
            switchMap(() => {
                return this.getRunningDesignInfo();
            })
        );
    }

    private userIsNotAuthenticated(): Observable<RunningDesignDTO> {
        if (this.refreshNotificationsInterval$) {
            this.refreshNotificationsInterval$.complete();
        }
        return of({} as RunningDesignDTO);
    }
}
